<template>
  <div class="m-4 notifications-list">
    <div class="d-flex align-items-center mb-3">
      <h5 class="mb-0">Notifications</h5>
    </div>
    <grid
      class=""
      ref="grid"
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="list"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    ></grid>
    <b-modal
      v-model="showPolicyEditor"
      centered
      ref="form-edit-policy-modal"
      title="Event notifications policy"
      header-bg-variant="primary"
      header-text-variant="white"
      no-close-on-backdrop
      @ok="updatePolicy"
      @cancel="clearState"
      @close="clearState"
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="ok()">
          Save
        </b-button>
      </template>
      <b-form-group label="Notify">
        <b-form-checkbox-group v-model="selectedNotifications" stacked>
          <b-form-checkbox v-if="hasPushOption" value="push"
            >System notification</b-form-checkbox
          >
          <b-form-checkbox v-if="hasEmailOption" value="email"
            >Email notification</b-form-checkbox
          >
          <b-form-checkbox v-if="hasSmsOption" value="sms"
            >SMS notification</b-form-checkbox
          >
        </b-form-checkbox-group>
      </b-form-group>
      <div v-if="privateNotification == false">
        <b-form-group
          for="usersSelect"
          :state="formErrors.users.length == 0 ? null : false"
          :invalid-feedback="formErrors.users.join(', ')"
        >
          <legend class="title-label pt-0" for="users-select">
            Users (up to {{ limitOfUsers }})
          </legend>
          <v-select
            multiple
            class=""
            placeholder=""
            :options="users"
            v-model="selectedUsers"
            v-on:input="limitNumberOfUsers"
            id="users-select"
          ></v-select>
        </b-form-group>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import { mapActions, mapState } from "vuex";
import eventNotificationPolicyServer from "../../server/eventNotificationPolicy";
import roleServer from "../../server/role";
import userServer from "../../server/user";

export default {
  name: "NotificationConfig",
  components: {
    Grid
  },
  data() {
    return {
      list: [],
      roles: [],
      users: [],
      editPolicyId: undefined,
      showPolicyEditor: false,
      selectedRoles: [],
      selectedCaseRoles: [],
      selectedUsers: [],
      hasEmailOption: false,
      hasSmsOption: false,
      hasPushOption: false,
      selectedNotifications: [],
      privateNotification: false,
      limitOfUsers: 3,
      formErrors: {
        users: []
      }
    };
  },
  mounted() {
    this.getEventNotificationPolicy();
    this.getRoles();
    this.fetchCaseRoles();
    this.getUsers();
  },
  methods: {
    ...mapActions("client", ["fetchCaseRoles"]),
    ...mapActions("layout", ["showAlertSuccess"]),
    async getEventNotificationPolicy() {
      this.list = await eventNotificationPolicyServer.query();
    },
    async getRoles() {
      const roles = await roleServer.query();
      this.roles = roles.map(role => ({
        value: role.id,
        label: role.name
      }));
    },
    async getUsers() {
      const users = await userServer.getMinimalUsers();
      this.users = users.map(user => ({
        value: user.id,
        label: this.formatUser(user)
      }));
    },
    async updatePolicy(modalEvent) {
      modalEvent.preventDefault();
      let data = Object.assign(
        {},
        this.list.find(x => x.id === this.editPolicyId),
        {
          sendPush: this.selectedNotifications.includes("push"),
          sendEmail: this.selectedNotifications.includes("email"),
          sendSms: this.selectedNotifications.includes("sms"),
          roleIds: this.selectedRoles.map(r => r.value),
          caseRoleIds: this.selectedCaseRoles.map(c => c.value),
          userIds: this.selectedUsers.map(u => u.value)
        }
      );

      await eventNotificationPolicyServer.update(this.editPolicyId, data);

      this.getEventNotificationPolicy();
      this.clearState();
      this.showAlertSuccess();
    },
    loadPolicyState(policy) {
      this.editPolicyId = policy.id;

      this.privateNotification = policy.privateNotification ?? false;

      if (policy.sendPush) this.selectedNotifications.push("push");
      if (policy.sendEmail) this.selectedNotifications.push("email");
      if (policy.sendSms) this.selectedNotifications.push("sms");

      this.hasEmailOption = policy.hasEmailOption;
      this.hasSmsOption = policy.hasSmsOption;
      this.hasPushOption = policy.hasPushOption;

      if (policy.roles) {
        this.selectedRoles.push(
          ...this.roles.filter(role =>
            policy.roles.map(x => x.id).includes(role.value)
          )
        );
      }

      if (policy.caseRoles) {
        this.selectedCaseRoles.push(
          ...this.caseRoles.filter(caseRole =>
            policy.caseRoles.map(x => x.id).includes(caseRole.value)
          )
        );
      }

      if (policy.users) {
        this.selectedUsers.push(
          ...this.users.filter(user =>
            policy.users.map(x => x.id).includes(user.value)
          )
        );
      }
    },
    clearState() {
      this.editPolicyId = undefined;
      this.selectedRoles.splice(0);
      this.selectedCaseRoles.splice(0);
      this.selectedUsers.splice(0);
      this.selectedNotifications.splice(0);
      this.showPolicyEditor = false;
    },
    limitNumberOfUsers(e) {
      if (e.length > this.limitOfUsers) {
        e.pop();
        this.formErrors.users.push(
          `Cannot add more than ${this.limitOfUsers} users`
        );
      } else {
        this.formErrors.users = [];
      }
    }
  },
  computed: {
    ...mapState("client", {
      caseRoles: state =>
        state.caseRoles.map(caseRole => ({
          value: caseRole.id,
          label: caseRole.name
        }))
    }),
    ...mapState("layout", {
      advancedOptions: state => state.advancedOptions
    }),
    columns() {
      let columns = [];

      columns.push({
        text: "Event name",
        field: "eventName",
        type: "template",
        width: 200,
        editor: false,
        template: data =>
          data.record.data.friendlyName ?? data.record.data.eventName,
        sortable(a, b) {
          const [eventA, eventB] = [
            a.data.eventName ?? "",
            b.data.eventName ?? ""
          ];
          return eventA.localeCompare(eventB);
        }
      });

      columns.push({
        text: "System",
        field: "push",
        type: "template",
        align: "center",
        editor: false,
        width: 80,
        template: data => {
          if (data.record.data.sendPush) {
            return `<i style="font-size: 19px;" class="fa fa-check text-success"></i>`;
          }

          return `<i style="font-size: 19px;" class="fa fa-times-circle text-danger"></i>`;
        },
        sortable: false
      });

      columns.push({
        text: "Email",
        field: "email",
        type: "template",
        align: "center",
        editor: false,
        width: 80,
        template: data => {
          if (data.record.data.sendEmail) {
            return `<i style="font-size: 19px;" class="fa fa-check text-success"></i>`;
          }

          return `<i style="font-size: 19px;" class="fa fa-times-circle text-danger"></i>`;
        },
        sortable: false
      });

      columns.push({
        text: "SMS",
        field: "sms",
        type: "template",
        align: "center",
        editor: false,
        width: 80,
        template: data => {
          if (data.record.data.sendSms) {
            return `<i style="font-size: 19px;" class="fa fa-check text-success"></i>`;
          }

          return `<i style="font-size: 19px;" class="fa fa-times-circle text-danger"></i>`;
        },
        sortable: false
      });

      columns.push({
        text: "Users",
        field: "users",
        type: "template",
        flex: 1,
        editor: false,
        template: data =>
          data.record.data.users?.map(r => this.formatUser(r)).join(", "),
        sortable: false
      });

      columns.push({
        text: "Actions",
        field: "actions",
        type: "action",
        cellCls: "pointer-cell",
        align: "right",
        width: 150,
        actions: [
          {
            tooltip: "Edit event notification policy",
            renderer: () =>
              `<img class="b-action-item ml-2 mr-1" src="${
                this.isStandalone()
                  ? `/icons/edit.svg`
                  : `/desktop/icons/edit.svg`
              }">`,
            onClick: ({ record }) => {
              this.showPolicyEditor = true;
              this.loadPolicyState(record.originalData);
            }
          }
        ]
      });

      return columns;
    }
  }
};
</script>

<style>
.notifications-list .b-grid-container .b-container {
  max-height: calc(100vh - 254px);
}
.title-label {
  font-size: 14px;
}
</style>
